import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";
import { ComplianceRow } from "@/components/ComplianceRow";
import { StatsBand, CardGrid, Eyebrow, Icon, Resources } from "@/components/ServiceSections";
import { IMAGES } from "@/content/services";

const INDUSTRY_STATS = [
  { value: "6", label: "Regulated sectors served end to end" },
  { value: "100%", label: "Engagements mapped to sector frameworks" },
  { value: "24/7", label: "Monitoring available in every sector" },
  { value: "180 days", label: "Minimum log retention where CERT-In applies" },
];

const SEGMENTS = [
  {
    icon: "Landmark",
    name: "Banking & Fintech",
    copy: "Payment infrastructure and core banking platforms demand continuous testing, PCI-DSS alignment and RBI cyber-framework compliance. We deliver VAPT, managed SOC and transaction-surface monitoring built for financial regulators.",
    frameworks: ["PCI-DSS", "SOC 2", "ISO 27001", "RBI Guidelines"],
    to: "/services/managed-security",
  },
  {
    icon: "HeartPulse",
    name: "Healthcare & Life Sciences",
    copy: "Patient data is among the most targeted assets on earth. We harden EHR platforms, hospital networks and health-tech products against ransomware and data theft, with HIPAA and DPDP-aligned controls throughout.",
    frameworks: ["HIPAA", "ISO 27001", "NIST CSF", "DPDP Act"],
    to: "/services/cloud-security",
  },
  {
    icon: "Rocket",
    name: "SaaS & High-Growth Tech",
    copy: "Enterprise buyers demand SOC 2 before they sign. We get high-growth companies audit-ready fast with penetration tests, cloud security and managed detection that scale with your ARR, not against it.",
    frameworks: ["SOC 2 Type II", "ISO 27001", "GDPR"],
    to: "/services/vapt",
  },
  {
    icon: "Shield",
    name: "Government & Defense",
    copy: "Public-sector and defense supply chains carry national stakes. We bring NIST 800-53 and CMMC-aligned architecture, continuous monitoring and incident response to mission-critical systems.",
    frameworks: ["NIST 800-53", "CMMC 2.0", "ISO 27001"],
    to: "/services/enterprise-landing-zone",
  },
  {
    icon: "ShoppingCart",
    name: "E-Commerce & Retail",
    copy: "Every transaction is an attack surface. We secure storefronts, payment flows and customer data platforms against card skimming, account takeover and bot-driven fraud.",
    frameworks: ["PCI-DSS", "SOC 2", "ISO 27001"],
    to: "/services/enterprise-xdr",
  },
  {
    icon: "Factory",
    name: "Manufacturing & Energy",
    copy: "OT and ICS environments can't afford downtime or breaches. We segment, monitor and test industrial environments with methodologies designed around operational constraints.",
    frameworks: ["NIST CSF", "IEC 62443", "ISO 27001"],
    to: "/services/enterprise-siem",
  },
];

const CROSS_SECTOR = [
  {
    icon: "Crosshair",
    title: "Sector-specific threat modelling",
    copy: "We start from the adversaries and techniques actually used against your industry, not a generic checklist, so testing and detection effort lands where your real exposure is.",
  },
  {
    icon: "Scale",
    title: "Framework mapping by default",
    copy: "Findings, controls and monitoring evidence are mapped to the frameworks your sector is audited against, so one engagement serves both security and audit.",
  },
  {
    icon: "Radar",
    title: "Monitoring built for your data",
    copy: "Detection content and retention policies are tuned to the regulated data your sector holds: cardholder data, patient records, personal data under DPDP or controlled unclassified information.",
  },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function Industries() {
  return (
    <div data-testid="industries-page">
      <PageHero
        breadcrumb={["Home", "Industries"]}
        pill="Industries We Serve"
        titleLines={[
          [{ t: "Purpose-built for " }, { t: "regulated", accent: true }],
          [{ t: "industries and high-growth teams." }],
        ]}
        description="Regulated industries and fast-growing organizations face the same reality: attackers don't care about your audit schedule. We build security operations that satisfy regulators and stop real threats."
        primaryCta={{ label: "Discuss Your Industry", to: "/contact" }}
        secondaryCta={{ label: "Our Services", to: "/services" }}
        image={IMAGES.dashboard}
      />

      <StatsBand stats={INDUSTRY_STATS} testId="industries-stats-band" />

      <section className="mx-auto max-w-7xl px-5 py-16 sm:py-24 sm:px-8 lg:px-16" data-testid="segments-grid">
        <Reveal>
          <Eyebrow>Sector Coverage</Eyebrow>
          <h2 className="mt-5 max-w-2xl font-display text-[1.75rem] font-bold leading-[1.2] tracking-tight text-white sm:text-4xl sm:leading-tight lg:text-[2.75rem] lg:leading-[1.12]">
            Your industry. Your threat model. <span className="text-[#8E9AEA]">Our specialty.</span>
          </h2>
        </Reveal>

        <div className="mt-10 sm:mt-14 grid gap-5 md:grid-cols-2">
          {SEGMENTS.map((s, i) => (
            <Reveal key={s.name} delay={(i % 2) * 0.08}>
              <Link
                to={s.to}
                className="glass-card group flex h-full flex-col p-6 sm:p-8"
                data-testid={`segment-${slug(s.name)}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg border border-white/10 bg-white/[0.05]">
                    <Icon name={s.icon} className="h-5 w-5 text-[#8E9AEA]" />
                  </span>
                  <ArrowUpRight className="h-5 w-5 text-[#8E9AEA] opacity-0 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:opacity-100" />
                </div>
                <h3 className="mt-6 font-display text-xl font-bold text-white">{s.name}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-[#DDE3F2]/90">{s.copy}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {s.frameworks.map((f) => (
                    <span
                      key={f}
                      className="rounded-md border border-white/15 bg-white/5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#DDE3F2]"
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#0D1322]" data-testid="cross-sector-section">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:py-24 sm:px-8 lg:px-16">
          <Reveal>
            <Eyebrow>What Every Sector Gets</Eyebrow>
            <h2 className="mt-5 max-w-2xl font-display text-[1.75rem] font-bold leading-[1.2] tracking-tight text-white sm:text-4xl sm:leading-tight lg:text-[2.75rem] lg:leading-[1.12]">
              Specialized where it matters, <span className="text-[#8E9AEA]">disciplined everywhere else.</span>
            </h2>
          </Reveal>
          <div className="mt-10 sm:mt-14">
            <CardGrid cards={CROSS_SECTOR} cols="md:grid-cols-3" testPrefix="cross-sector" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 sm:py-24 sm:px-8 lg:px-16" data-testid="compliance-coverage">
        <Reveal>
          <div className="glass-card flex flex-col items-start justify-between gap-6 p-6 sm:p-8 sm:flex-row sm:items-center sm:p-10">
            <div>
              <Eyebrow>Compliance Coverage</Eyebrow>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-[#DDE3F2] sm:text-base">
                Every engagement is mapped to the frameworks your auditors, customers and regulators actually ask
                about. Tell us your regulatory landscape and we'll show you the shortest defensible path.
              </p>
            </div>
            <Link to="/contact" className="btn-primary shrink-0" data-testid="industries-cta">
              Map My Compliance Path
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <ComplianceRow className="mt-10" />
        </Reveal>
      </section>

      <Resources resources={[]} pagePath="/industries" />

      <CTABand
        title={{ pre: "Security built for ", accent: "your sector." }}
        copy="Tell us your regulatory landscape and we'll show you exactly how Lanvaus secures organizations like yours."
        primary={{ label: "Get a Consultation", to: "/contact" }}
        secondary={{ label: "View All Services", to: "/services" }}
      />
    </div>
  );
}
